import { ChevronLeft, ChevronRight, Minus, Plus, PenLine } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { FieldValue, SigningForm } from './model'

export function DocumentPreview({
  form,
  values,
  selectedField,
  onSelectField,
  page,
  onPageChange,
  revealSelection,
  readOnly = false,
  signature,
}: {
  form: SigningForm
  values: Record<string, FieldValue | undefined>
  selectedField: string | null
  onSelectField: (name: string) => void
  page: number
  onPageChange: (page: number) => void
  revealSelection: boolean
  readOnly?: boolean
  signature?: { name: string; date: string }
}) {
  const [zoom, setZoom] = useState(100)
  const scrollRef = useRef<HTMLDivElement>(null)
  const selectedRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!revealSelection || !selectedField) return
    const container = scrollRef.current
    const field = selectedRef.current
    if (!container || !field) return
    const bounds = container.getBoundingClientRect()
    const target = field.getBoundingClientRect()
    if (target.top < bounds.top || target.bottom > bounds.bottom)
      container.scrollBy({
        top: target.top - bounds.top - bounds.height / 3,
        behavior: 'smooth',
      })
  }, [selectedField, page, revealSelection])
  const pages = form.pages ?? []
  const activePage = pages[Math.min(page, pages.length - 1)]
  const pageWidth = activePage?.width ?? 612
  const pageHeight = activePage?.height ?? 792
  if (!pages.length)
    return (
      <p className="p-6 text-sm text-muted-foreground">
        Document preview is not available for this template.
      </p>
    )
  return (
    <section
      aria-label="Document preview"
      className="flex min-h-0 flex-1 flex-col">
      <div className="relative flex min-h-0 flex-1">
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-auto bg-muted/25 px-4 pb-4 pt-0">
          <div
            className="relative mx-auto bg-white shadow-sm"
            style={{ width: `${zoom}%`, containerType: 'inline-size' }}>
            <img
              src={activePage?.url}
              alt={`${form.name}, page ${page + 1}`}
              className="block w-full"
              style={{ aspectRatio: `${pageWidth}/${pageHeight}` }}
            />
            {form.fields
              .filter((field) => field.page === page)
              .map((field) => {
                const [x0, y0, x1, y1] = field.rect
                const value = values[field.name]
                if (readOnly)
                  return (
                    <span
                      key={field.name}
                      title={field.label}
                      className="absolute flex items-center overflow-hidden text-[1.8cqw] leading-none text-black"
                      style={{
                        left: `${(x0 / pageWidth) * 100}%`,
                        top: `${((pageHeight - y1) / pageHeight) * 100}%`,
                        width: `${((x1 - x0) / pageWidth) * 100}%`,
                        height: `${((y1 - y0) / pageHeight) * 100}%`,
                      }}>
                      <span className="truncate px-[0.25cqw]">
                        {value === true
                          ? '✓'
                          : value === false
                            ? ''
                            : (value ?? '')}
                      </span>
                    </span>
                  )
                return (
                  <button
                    key={field.name}
                    ref={selectedField === field.name ? selectedRef : undefined}
                    type="button"
                    aria-label={`Locate ${field.label}`}
                    title={field.label}
                    onClick={() => onSelectField(field.name)}
                    className={cn(
                      'absolute flex items-center overflow-hidden rounded-[1px] border text-left text-[1.8cqw] leading-none text-blue-900 transition-colors',
                      selectedField === field.name
                        ? 'border-blue-600 bg-blue-100/80 ring-1 ring-blue-500'
                        : 'border-blue-400/25 bg-blue-50/65 hover:border-blue-500'
                    )}
                    style={{
                      left: `${(x0 / pageWidth) * 100}%`,
                      top: `${((pageHeight - y1) / pageHeight) * 100}%`,
                      width: `${((x1 - x0) / pageWidth) * 100}%`,
                      height: `${((y1 - y0) / pageHeight) * 100}%`,
                    }}>
                    <span className="truncate px-[0.25cqw]">
                      {value === true
                        ? '✓'
                        : value === false
                          ? ''
                          : (value ?? '')}
                    </span>
                  </button>
                )
              })}
            {(form.signingSlots ?? [])
              .filter((slot) => slot.page === page)
              .map((slot) => {
                const [x0, y0, x1, y1] = slot.rect
                if (readOnly)
                  return (
                    <span
                      key={slot.type}
                      aria-label={slot.label}
                      className={cn(
                        'absolute flex items-center px-[0.5cqw] text-[1.8cqw] leading-none text-black',
                        !signature &&
                          'border border-dashed border-violet-400/60 bg-violet-50 text-violet-800',
                        signature &&
                          slot.type === 'signature' &&
                          'font-serif text-[2.8cqw] italic'
                      )}
                      style={{
                        left: `${(x0 / pageWidth) * 100}%`,
                        top: `${((pageHeight - y1) / pageHeight) * 100}%`,
                        width: `${((x1 - x0) / pageWidth) * 100}%`,
                        height: `${((y1 - y0) / pageHeight) * 100}%`,
                      }}>
                      {signature
                        ? slot.type === 'signature'
                          ? signature.name
                          : signature.date
                        : slot.type === 'signature'
                          ? 'Signature'
                          : 'Date'}
                    </span>
                  )
                return (
                  <button
                    key={slot.type}
                    ref={selectedField === slot.type ? selectedRef : undefined}
                    type="button"
                    onClick={() => onSelectField(slot.type)}
                    aria-label={slot.label}
                    className={cn(
                      'absolute flex items-center gap-[0.6cqw] border border-dashed border-violet-500/60 bg-violet-100/80 px-[0.5cqw] text-[1.8cqw] leading-none text-violet-900',
                      selectedField === slot.type &&
                        'border-blue-600 bg-blue-100/80 ring-1 ring-blue-500'
                    )}
                    style={{
                      left: `${(x0 / pageWidth) * 100}%`,
                      top: `${((pageHeight - y1) / pageHeight) * 100}%`,
                      width: `${((x1 - x0) / pageWidth) * 100}%`,
                      height: `${((y1 - y0) / pageHeight) * 100}%`,
                    }}>
                    {slot.type === 'signature' && (
                      <PenLine className="size-[2cqw]" />
                    )}
                    {slot.type === 'signature' ? 'Signature' : 'Date'}
                  </button>
                )
              })}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 px-3 py-2">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Previous page"
            disabled={page === 0}
            onClick={() => onPageChange(page - 1)}>
            <ChevronLeft />
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page + 1} of {pages.length}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Next page"
            disabled={page >= pages.length - 1}
            onClick={() => onPageChange(page + 1)}>
            <ChevronRight />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Zoom out"
            disabled={zoom <= 75}
            onClick={() => setZoom(zoom - 25)}>
            <Minus />
          </Button>
          <span className="w-10 text-center text-xs tabular-nums">{zoom}%</span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Zoom in"
            disabled={zoom >= 175}
            onClick={() => setZoom(zoom + 25)}>
            <Plus />
          </Button>
        </div>
      </div>
    </section>
  )
}
